import type { Tables } from '@/types/database.types';
import type { FrontendMenuItem } from '@/types/menu.types';

export type TicketingFestival = Tables<'festivals'> & {
    early_bird_ticket_id?: string | null;
    gift_redemption_only?: boolean;
};

export type FestivalAccess = {
    is_published: boolean;
    early_bird_ticket_id: string | null;
    has_gift_redemption_access: boolean;
};

export function isEarlyBirdOnly(festival: TicketingFestival | FestivalAccess | null): boolean {
    return festival?.is_published === false && !!festival.early_bird_ticket_id;
}

export function parseFestivalAccess(value: unknown): FestivalAccess | null {
    if (!value || typeof value !== 'object') return null;
    const access = value as Record<string, unknown>;
    if (typeof access.is_published !== 'boolean') return null;
    if (access.early_bird_ticket_id !== null && typeof access.early_bird_ticket_id !== 'string') {
        return null;
    }
    return {
        is_published: access.is_published,
        early_bird_ticket_id: access.early_bird_ticket_id,
        has_gift_redemption_access: access.has_gift_redemption_access === true,
    };
}

export function isTicketingRoute(segments: string[]): boolean {
    if (!['en', 'fr'].includes(segments[1])) return false;
    const route = segments.slice(2).join('/');
    return (
        [
            '',
            'about',
            'partners',
            'insights',
            'account',
            'account/my-tickets',
            'account/order-history',
            'account/travel-info',
            'account/accreditation',
            'tickets-and-venues',
            'sign-in',
            'sign-up',
            'forgot-password',
            'reset-password',
            'checkout',
            'checkout/payment',
        ].includes(route) ||
        (segments[2] === 'insights' && segments.length === 4)
    );
}

export const checkoutSignIn = '/sign-in/?returnTo=checkout';

export function ticketingAuthHref(path: string, returnToCheckout: boolean, giftQuery = ''): string {
    if (returnToCheckout) {
        return `${path}?returnTo=checkout${giftQuery ? `&${giftQuery.slice(1)}` : ''}`;
    }
    return `${path}${giftQuery}`;
}

export function filterEarlyBirdMenu(items: FrontendMenuItem[], locale: string): FrontendMenuItem[] {
    const canOpen = (link?: string) => {
        if (!link || link === '#') return false;
        if (/^(https?:\/\/|mailto:|tel:)/i.test(link)) return true;
        const segments = link.split(/[?#]/)[0].split('/').filter(Boolean);
        return isTicketingRoute(['festival', locale, ...segments]);
    };

    return items.flatMap((item) => {
        const children = item.items.filter((child) => canOpen(child.link));
        const link = canOpen(item.link) ? item.link : undefined;
        return link || children.length > 0 ? [{ ...item, link, items: children }] : [];
    });
}

type EarlyBirdProduct = Pick<
    Tables<'tickets'>,
    'id' | 'festival_id' | 'is_active' | 'is_gift' | 'purchase_location'
> & { gift_target_ticket_id?: string | null };

export function isEarlyBirdProduct(
    ticket: EarlyBirdProduct,
    festival: TicketingFestival | null,
): boolean {
    if (
        !festival?.early_bird_ticket_id ||
        ticket.festival_id !== festival.id ||
        !ticket.is_active ||
        !['ONLINE', 'BOTH'].includes(ticket.purchase_location)
    ) {
        return false;
    }
    return (
        (!ticket.is_gift && ticket.id === festival.early_bird_ticket_id) ||
        (ticket.is_gift === true && ticket.gift_target_ticket_id === festival.early_bird_ticket_id)
    );
}

export function isEarlyBirdCartItem(
    item: { ticket: EarlyBirdProduct; screening?: unknown },
    festival: TicketingFestival | null,
): boolean {
    return !item.screening && isEarlyBirdProduct(item.ticket, festival);
}
